import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { BookOpen, RefreshCw, Send, Trash2, AlertCircle, Lock } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const fmt = (n, d = 2) => (n ?? 0).toLocaleString("en-ZA", { minimumFractionDigits: d, maximumFractionDigits: d });

const today = () => new Date().toISOString().slice(0, 10);
const weekAgo = () => { const d = new Date(); d.setDate(d.getDate() - 7); return d.toISOString().slice(0, 10); };

export default function Journals() {
  const { user } = useAuth();
  const isOwner = user?.role === "Owner";
  const [zohoStatus, setZohoStatus] = useState(null);
  const [settings, setSettings] = useState({ cogs_account_id: "", inventory_account_id: "" });
  const [accounts, setAccounts] = useState([]);
  const [journals, setJournals] = useState([]);
  const [preview, setPreview] = useState(null);
  const [periodFrom, setPeriodFrom] = useState(weekAgo());
  const [periodTo, setPeriodTo] = useState(today());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const load = async () => {
    setBusy(true); setError("");
    try {
      const [z, j] = await Promise.all([
        api.get("/zoho/status"),
        api.get("/journals"),
      ]);
      setZohoStatus(z.data);
      setJournals(j.data);
      const s = z.data?.settings || {};
      setSettings({
        cogs_account_id: s.cogs_account_id || "",
        cogs_account_name: s.cogs_account_name || "",
        inventory_account_id: s.inventory_account_id || "",
        inventory_account_name: s.inventory_account_name || "",
        last_journal_through: s.last_journal_through || "",
      });
      if (s.last_journal_through) {
        const next = new Date(s.last_journal_through);
        next.setDate(next.getDate() + 1);
        setPeriodFrom(next.toISOString().slice(0, 10));
      }
      if (z.data?.connected) {
        // Need full chart-of-accounts (inventory accounts are not expense/COGS),
        // otherwise the Inventory dropdown is empty.
        const acc = await api.get("/zoho/accounts?only_expense=false");
        setAccounts(acc.data);
      }
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };
  useEffect(() => { load(); }, []);

  const saveSettings = async () => {
    setBusy(true); setError("");
    try {
      const cogs = accounts.find((a) => a.account_id === settings.cogs_account_id);
      const inv = accounts.find((a) => a.account_id === settings.inventory_account_id);
      await api.put("/zoho/settings", {
        cogs_account_id: settings.cogs_account_id || null,
        cogs_account_name: cogs?.account_name || null,
        inventory_account_id: settings.inventory_account_id || null,
        inventory_account_name: inv?.account_name || null,
        journal_cadence: "weekly",
      });
      await load();
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  const runPreview = async () => {
    setBusy(true); setError("");
    try {
      const { data } = await api.get(`/journals/preview?period_from=${periodFrom}&period_to=${periodTo}`);
      setPreview(data);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  const saveDraft = async () => {
    setBusy(true); setError("");
    try {
      await api.post("/journals", { period_from: periodFrom, period_to: periodTo });
      setPreview(null);
      await load();
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  const post = async (id) => {
    if (!window.confirm("Approve and post this journal to Zoho Books? This cannot be undone from inside this app.")) return;
    setBusy(true); setError("");
    try {
      await api.post(`/journals/${id}/approve-and-post`);
      await load();
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this draft?")) return;
    await api.delete(`/journals/${id}`);
    await load();
  };

  if (zohoStatus && !zohoStatus.connected) {
    // Show a warning banner but DO NOT hide existing drafts — Owner still needs
    // to review draft state, retry errors, and see locked/posted history.
    // The drafting form (which needs account dropdowns) is hidden until Zoho is back.
    // The Approve&post button on each draft will return a clear error if Zoho is down.
  }

  const zohoOffline = zohoStatus && !zohoStatus.connected;

  const cogsAccts = accounts.filter((a) => a.account_type === "cost_of_goods_sold");
  const invAccts = accounts.filter((a) => ["other_current_asset", "stock"].includes(a.account_type));

  return (
    <div data-testid="journals-page">
      <PageHeader
        eyebrow="Bookkeeping"
        title="Manual Journals"
        description="Draft Dr COGS / Cr Inventory journals from roasted volume × FIFO unit cost. Approve to post to Zoho Books."
        actions={
          <button onClick={load} disabled={busy} data-testid="journals-refresh"
            className="inline-flex items-center gap-2 px-4 py-2 border border-border rounded text-sm hover:bg-secondary">
            <RefreshCw className={`w-4 h-4 ${busy ? "animate-spin" : ""}`} /> Refresh
          </button>
        }
      />

      {error && <div className="bg-destructive/10 border border-destructive/30 rounded p-3 text-sm mb-4">{error}</div>}

      {zohoOffline && (
        <div className="bg-[hsl(31,65%,53%)]/10 border border-[hsl(31,65%,53%)]/30 rounded p-4 mb-6 flex items-start gap-2"
             data-testid="zoho-offline-banner">
          <AlertCircle className="w-4 h-4 text-[hsl(31,65%,53%)] mt-0.5 shrink-0" />
          <div>
            <p className="font-semibold text-[hsl(31,65%,53%)] mb-0.5">Zoho Books not connected</p>
            <p className="text-sm">
              You can still review existing drafts and posted history below.
              Connect Zoho at <a href="/settings" className="text-primary underline">Settings → Zoho Books</a> before approving new journals.
            </p>
          </div>
        </div>
      )}

      {/* Account mapping + Draft creator only when Zoho is connected */}
      {!zohoOffline && (<>
      <section className="bg-card border border-border rounded p-5 mb-6">
        <div className="flex items-center gap-2 mb-3">
          <BookOpen className="w-4 h-4 text-primary" />
          <h3 className="font-display text-lg font-bold">Account mapping</h3>
        </div>
        <p className="text-xs text-muted-foreground mb-4">
          Each posted journal will Dr the COGS account and Cr the Inventory account for the total value. Map them once below — Brothers Coffee already has these accounts in Zoho.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <p className="label-tiny mb-1">COGS account (debit)</p>
            <select value={settings.cogs_account_id} data-testid="map-cogs"
              onChange={(e) => setSettings({ ...settings, cogs_account_id: e.target.value })}
              className="w-full px-3 py-2 border border-border rounded text-sm">
              <option value="">— choose —</option>
              {cogsAccts.map((a) => <option key={a.account_id} value={a.account_id}>{a.account_name}</option>)}
            </select>
          </div>
          <div>
            <p className="label-tiny mb-1">Inventory account (credit)</p>
            <select value={settings.inventory_account_id} data-testid="map-inv"
              onChange={(e) => setSettings({ ...settings, inventory_account_id: e.target.value })}
              className="w-full px-3 py-2 border border-border rounded text-sm">
              <option value="">— choose —</option>
              {invAccts.map((a) => <option key={a.account_id} value={a.account_id}>{a.account_name}</option>)}
            </select>
          </div>
        </div>
        <div className="mt-3 flex justify-end">
          <button onClick={saveSettings} disabled={busy || !settings.cogs_account_id || !settings.inventory_account_id} data-testid="map-save"
            className="px-4 py-2 bg-primary text-primary-foreground rounded text-sm font-semibold hover:bg-[hsl(20,41%,32%)] disabled:opacity-60">
            Save mapping
          </button>
        </div>
        {settings.last_journal_through && (
          <p className="text-xs text-muted-foreground mt-3 font-mono">Last posted journal covered roasts through {settings.last_journal_through}.</p>
        )}
      </section>

      {/* Generate draft */}
      <section className="bg-card border border-border rounded p-5 mb-6">
        <h3 className="font-display text-lg font-bold mb-3">Generate draft journal</h3>
        <div className="flex flex-wrap gap-3 items-end mb-4">
          <div>
            <p className="label-tiny mb-1">Period from</p>
            <input type="date" data-testid="period-from" value={periodFrom}
              onChange={(e) => setPeriodFrom(e.target.value)}
              className="px-3 py-2 border border-border rounded font-mono text-sm" />
          </div>
          <div>
            <p className="label-tiny mb-1">Period to (exclusive)</p>
            <input type="date" data-testid="period-to" value={periodTo}
              onChange={(e) => setPeriodTo(e.target.value)}
              className="px-3 py-2 border border-border rounded font-mono text-sm" />
          </div>
          <button onClick={runPreview} disabled={busy} data-testid="journal-preview"
            className="px-4 py-2 border border-border rounded text-sm hover:bg-secondary disabled:opacity-60">
            Preview
          </button>
          {preview && preview.total_amount > 0 && (
            <button onClick={saveDraft} disabled={busy || !settings.cogs_account_id || !settings.inventory_account_id} data-testid="journal-save"
              className="px-4 py-2 bg-primary text-primary-foreground rounded text-sm font-semibold hover:bg-[hsl(20,41%,32%)] disabled:opacity-60">
              Save as draft
            </button>
          )}
        </div>

        {preview && (
          <div className="bg-secondary/50 rounded p-4" data-testid="journal-preview-result">
            <div className="flex items-baseline justify-between mb-3">
              <p className="text-sm">
                <strong>{preview.lines.length}</strong> origins · <strong>{fmt(preview.total_kg, 1)}</strong> kg roasted
              </p>
              <p className="font-display text-2xl font-bold">R {fmt(preview.total_amount)}</p>
            </div>
            <p className="text-[11px] text-muted-foreground mb-3 italic">
              Cost is calculated using true FIFO drawdown across each origin's lots (matches the Green Inventory valuation method). The R/kg shown is the effective blended rate for the period.
            </p>
            {preview.lines.length === 0 ? (
              <p className="text-sm text-muted-foreground">No roasts in this period.</p>
            ) : (
              <table className="w-full text-xs">
                <thead><tr className="text-left text-muted-foreground">
                  <th className="py-1">Origin</th><th className="py-1 text-right">Roasts</th>
                  <th className="py-1 text-right">Green kg</th><th className="py-1 text-right">R/kg</th>
                  <th className="py-1 text-right">Amount</th>
                </tr></thead>
                <tbody>
                  {preview.lines.map((l) => (
                    <tr key={l.origin_id} className="border-t border-border">
                      <td className="py-1.5">{l.origin_name}</td>
                      <td className="py-1.5 text-right font-mono">{l.roasts}</td>
                      <td className="py-1.5 text-right font-mono">{fmt(l.kg, 1)}</td>
                      <td className="py-1.5 text-right font-mono">{fmt(l.unit_cost_per_kg)}</td>
                      <td className="py-1.5 text-right font-mono">R {fmt(l.amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </section>
      </>)}

      {/* Drafts + history (always visible — even when Zoho is offline) */}
      <section className="bg-card border border-border rounded">
        <div className="px-5 py-3 border-b border-border">
          <h3 className="font-display text-lg font-bold">Drafts &amp; posted journals</h3>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-secondary">
            <tr>
              <th className="text-left p-3 label-tiny">Period</th>
              <th className="text-right p-3 label-tiny">Kg</th>
              <th className="text-right p-3 label-tiny">Amount (R)</th>
              <th className="text-left p-3 label-tiny">Status</th>
              <th className="text-left p-3 label-tiny">Zoho Journal #</th>
              <th className="text-right p-3 label-tiny">Action</th>
            </tr>
          </thead>
          <tbody>
            {journals.length === 0 && <tr><td colSpan="6" className="p-8 text-center text-muted-foreground">No journals yet.</td></tr>}
            {journals.map((j) => (
              <JournalRow key={j.id} j={j} isOwner={isOwner} onPost={post} onDelete={del} />
            ))}
          </tbody>
        </table>
      </section>
    </div>
  );
}

function JournalRow({ j, isOwner, onPost, onDelete }) {
  return (
    <>
      <tr className="border-t border-border" data-testid={`journal-${j.id}`}>
        <td className="p-3 font-mono text-xs">{j.period_from} → {j.period_to}</td>
        <td className="p-3 text-right font-mono">{fmt(j.total_kg, 1)}</td>
        <td className="p-3 text-right font-mono">{fmt(j.total_amount)}</td>
        <td className="p-3">
          <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-[10px] font-semibold ${
            j.status === "posted" ? "bg-[hsl(132,18%,36%)]/10 text-[hsl(132,18%,36%)]"
                                  : "bg-[hsl(31,65%,53%)]/15 text-[hsl(31,65%,53%)]"
          }`}>
            {j.status === "posted" && <Lock className="w-2.5 h-2.5" />}
            {j.status}
          </span>
          {j.status === "posted" && j.approved_by_email && (
            <p className="text-[10px] text-muted-foreground mt-1">
              by {j.approved_by_email}
              {j.approved_at && ` · ${new Date(j.approved_at).toLocaleDateString("en-ZA")}`}
            </p>
          )}
        </td>
        <td className="p-3 font-mono text-xs text-muted-foreground">{j.zoho_journal_number || "—"}</td>
        <td className="p-3 text-right">
          {j.status === "draft" && (
            <div className="inline-flex gap-1">
              {isOwner ? (
                <button onClick={() => onPost(j.id)} data-testid={`post-${j.id}`}
                  className="inline-flex items-center gap-1 px-3 py-1.5 bg-primary text-primary-foreground rounded text-xs font-semibold hover:bg-[hsl(20,41%,32%)]">
                  <Send className="w-3 h-3" /> Approve &amp; post
                </button>
              ) : (
                <span className="text-[10px] text-muted-foreground italic px-2"
                      title="Only an Owner can approve and post journals to Zoho">
                  Awaiting Owner approval
                </span>
              )}
              <button onClick={() => onDelete(j.id)} data-testid={`del-${j.id}`}
                title="Delete this draft"
                className="p-1.5 text-destructive border border-destructive/30 rounded hover:bg-destructive/10">
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          )}
          {j.status === "posted" && (
            <span className="text-[10px] text-muted-foreground italic"
                  title="Posted journals are locked. Reverse via Zoho if needed.">
              Locked
            </span>
          )}
        </td>
      </tr>
      {j.status === "draft" && j.last_post_error && (
        <tr className="bg-destructive/5">
          <td colSpan="6" className="px-3 pb-3 pt-0">
            <div className="flex items-start gap-2 text-xs text-destructive" data-testid={`error-${j.id}`}>
              <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
              <span>
                <strong>Previous post attempt failed:</strong>{" "}
                {j.last_post_error}
                {j.last_post_error_at && (
                  <span className="text-muted-foreground">
                    {" "}· {new Date(j.last_post_error_at).toLocaleString("en-ZA")}
                  </span>
                )}
              </span>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}
